import Drawer from "@material-ui/core/Drawer";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { useAuthContext } from "contexts/auth";
import { useTranslation } from "react-i18next";
import {
  FiCalendar,
  FiClipboard,
  FiDollarSign,
  FiMail,
  FiSettings,
  FiTruck,
  FiUser,
  FiPenTool,
} from "react-icons/fi";
import { AiOutlineTool } from "react-icons/ai";

// Move styled component outside to avoid dynamic creation warnings
const NavLink = styled(Link)`
  color: white;
  text-decoration: none;
  padding: 0.25rem 1rem 0.25rem 2.5rem;
  display: ${(props) => props.$display || "flex"};
  font-size: 1rem;
  :hover {
    background-color: #017195;
  }
`;

export default function DrawerCon({ anchor, setAnchor, linkClicked }) {
  const { t } = useTranslation();
  const tBase = "views.navigation.drawer";
  const tr = (key) => t(`${tBase}.${key}`);

  const { permissions, company_features = {} } = useAuthContext();
  const allowedPages = permissions.pages || [""];

  const ROUTE_TO_COMPANY_FEATURE = {
    "/outreach": "outreach",
  };

  const isCompanyFeatureAllowed = (route) => {
    const featureKey = ROUTE_TO_COMPANY_FEATURE[route];
    if (!featureKey) return true;
    return !!company_features[featureKey];
  };

  const navShow = (page) => {
    const raw = typeof page === "string" || page instanceof String
      ? page
      : page.pathname;
    const route = "/" + raw.split("/")[1];
    if (!allowedPages.includes(route)) return "none";
    if (!isCompanyFeatureAllowed(route)) return "none";
    return "flex";
  };

  const menuItems = [
    {
      header: "CLIENT",
      icon: <FiUser />,
      links: [
        {
          name: "Customers",
          to: "/customers",
        },
        {
          name: "Properties",
          to: "/property",
        },
        {
          name: "Service Plans",
          to: "/service-plan-dashboard",
        }
      ]
    },
    {
      header: "SCHEDULING",
      icon: <FiCalendar />,
      links: [
        {
          name: "Appointments",
          to: "/appointments",
        },
        {
          name: "Calendar",
          to: "/calendar",
        },
      ]
    },
    {
      header: "SALES",
      icon: <FiClipboard />,
      links: [
        {
          name: "Requests",
          to: "/requests",
        },
        {
          name: "Estimates",
          to: "/estimates",
        },
      ]
    },
    {
      header: "FINANCE",
      icon: <FiDollarSign />,
      links: [
        {
          name: "Invoices",
          to: "/invoices",
        },
        {
          name: "Payments",
          to: "/payments",
        },
      ]
    },
    {
      header: "COMMERCIAL",
      icon: <FiPenTool />,
      links: [
        {
          name: "Projects",
          to: "/projects",
        },
        {
          name: "Quotes",
          to: "/quotes",
        },
      ]
    },
    {
      header: "WAREHOUSE",
      icon: <FiTruck />,
      links: [
        {
          name: "Order",
          to: "/order",
        },
        {
          name: "Warehouse",
          to: "/warehouse",
        },
        {
          name: "Purchasing",
          to: "/purchasing",
        },
        {
          name: "Vendors",
          to: "/vendors",
        },
        {
          name: "Products",
          to: "/products",
        },
      ]
    },
    {
      header: "EQUIPMENT",
      icon: <AiOutlineTool />,
      links: [
        {
          name: "Manage Equipment",
          to: "/equipment",
        },
        {
          name: "Vehicle Check-Out",
          to: "/vehicleCheckout",
        },
        {
          name: "Repair Request Management",
          to: "/repair_request",
        },
        {
          name: "Vehicle Inspection Dashboard",
          to:"/vehicle_inspection/dashboard"
        }
      ]
    },
    {
      header: "EMPLOYEES",
      icon: <FiUser />,
      links: [
        {
          name: "Team Dashboard",
          to: "/team",
        },
        {
          name: "Employees",
          to: "/admin/employees",
        },
        {
          name: "Absent",
          to: "/absent",
        },
        {
          name: "Historical Team",
          to: "/historicalteam",
        },
        {
          name: "Time Clock",
          to: "/time-clock",
        },
        {
          name: "Production",
          to: "/production",
        },
      ]
    },
    {
      header: "COMMUNICATIONS",
      icon: <FiMail />,
      links: [
        {
          name: "Campaigns",
          to: "/outreach/campaigns",
        },
        {
          name: "Templates",
          to: "/outreach/templates",
        },
        {
          name: "Unsubscribed",
          to: "/outreach/unsubscribed",
        },
      ]
    },
    {
      header: "MANAGE",
      icon: <FiSettings />,
      links: [
        {
          name: "Question Builder",
          to: "/settings/questions",
        },
        {
          name: "Warehouse Management",
          to: "/warehouseManagement",
        },
        {
          name: "Category Management",
          to: "/category_management",
        },
        {
          name: "Reports",
          to: "/reports",
        },
        {
          name: "Settings",
          to:"/admin"
        }
      ]
    },
  ]

  return (
    <Drawer anchor="left" open={anchor} onClose={() => setAnchor(false)}>
      <div className="scrollbar">
        <div className="min-h-screen pb-12 text-white bg-slate-900">
          <div className="flex p-4 space-x-2">
            <div className="text-xl text-primary">EServ</div>
          </div>
          <div className="scrollbar:!w-1.5 w-[300px] flex flex-col space-y-4">
            {menuItems
              .filter((item) => item.links.some((link) => navShow(link.to) === "flex"))
              .map((item, index) => (
              <div key={index}>
                <div className="flex items-center px-4 py-1 space-x-2 text-sm text-slate-300">
                  {item.icon}
                  <span>{tr(item.header)}</span>
                </div>
                {item.links.map((link, index) => (
                  <NavLink
                    key={index}
                    name={link.name}
                    to={link.to}
                    $display={navShow(link.to)}
                    onClick={() => linkClicked(link.name)}
                  >
                    {tr(link.name)}
                  </NavLink>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>  
    </Drawer>
  );
}